import {
  BookmarkIcon,
  CaptionsIcon,
  CornerUpRightIcon,
  HeartIcon,
  LanguagesIcon,
} from "lucide-react";
import React, { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useSwiper } from "swiper/react";
import { AnimatePresence, motion } from "framer-motion";
import { useShallow } from "zustand/react/shallow";
import { useCurrentEpisodeStore } from "../hooks/useCurrentEpisode";
import EpisodesList from "./EpisodesList";
import type Swiper from "swiper";
import type { GetShowQuery } from "@/codegen/graphql/generated/graphql";
import type { VideoVerticalPlayerSideControls } from "@/components/VideoVerticalPlayer/components/PlayerSideControl";
import { Separator } from "@/components/ui/separator";
import useFormat from "@/hooks/useFormat";
import useBookmark from "@/hooks/useBookmark";
import useEpisodeLike from "@/hooks/useEpisodeLike";
import PlayerSideControl from "@/components/VideoVerticalPlayer/components/PlayerSideControl";
import { cn } from "@/lib/utils";
import { useVideoPlayerStore } from "@/stores/useVideoPlayer";
import PlayerPreferenceModal from "@/components/VideoVerticalPlayer/components/PlayerPreferenceModal";
import { useAppStore } from "@/stores/useApp";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { buildShowUrl } from "@/lib/showSlug";
import { isShareSupported, share } from "@/lib/share";
import { useTracking } from "@/components/ClientAnalytics";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

export const EpisodeDesktopControlsSkeleton: React.FC = () => (
  <div className="relative flex h-full w-full max-w-sm flex-col items-start gap-6 lg:max-h-[1024px]">
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col justify-between gap-4 py-3">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-12" />
      </div>
      <div className="flex flex-col gap-3 py-2">
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-2 w-1/2" />
      </div>
    </div>
    <Separator />
    <div className="flex w-full items-start justify-center gap-10 transition-all">
      <Skeleton className="h-10 w-10" />
      <Skeleton className="h-10 w-10" />
    </div>
    <Separator />
    <div className="grid w-full grid-cols-5 gap-2">
      {Array.from({ length: 20 }).map((_, index) => (
        <Skeleton className="h-14 w-full rounded-sm" key={index} />
      ))}
    </div>
  </div>
);

export const EpisodeDesktopControlsDescription: React.FC<{
  description: string;
}> = ({ description }) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const descriptionTooLong = description.length > 140;

  return (
    <p className={cn("text-md inline")}>
      <span
        className={cn(
          showMore && descriptionTooLong ? "line-clamp-none" : "line-clamp-3",
        )}
      >
        {description}
      </span>
      {descriptionTooLong && (
        <span className="inline-block">
          <Button
            variant="link"
            size="sm"
            className="text-md inline h-fit p-0"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore
              ? t("player_description_see_less_button")
              : t("player_description_see_more_button")}
          </Button>
        </span>
      )}
    </p>
  );
};

export interface EpisodeDesktopControlsProps extends React.ComponentProps<"div"> {
  /** The player's own pick, so the list and the mobile dialog move the swiper the same way. */
  onEpisodeSelect: (
    swiper: Swiper,
    episode: GetShowQuery["show"]["episodes"][number],
    index: number,
  ) => void;
}

const EpisodeDesktopControls: React.FC<EpisodeDesktopControlsProps> = ({
  className,
  onEpisodeSelect,
  ...props
}) => {
  const { t } = useTranslation();
  const { series } = useParams({ strict: false });
  const { formatViewsNumber } = useFormat();
  const { currentEpisode: episode, currentShow: show } =
    useCurrentEpisodeStore();

  const swiper = useSwiper();
  const { trackAmplitudeEvent } = useTracking();

  const { subtitles, audioTracks, hlsContext } = useVideoPlayerStore(
    useShallow((s) => ({
      subtitles: s.subtitles,
      audioTracks: s.audioTracks,
      hlsContext: s.hlsContext,
    })),
  );

  const { setAudioPreference, setSubtitlesPreference, disableSubtitles } =
    useAppStore(
      useShallow((s) => ({
        setAudioPreference: s.setAudioPreference,
        setSubtitlesPreference: s.setSubtitlesPreference,
        disableSubtitles: s.disableSubtitles,
      })),
    );

  const [openSubtitleModal, setOpenSubtitleModal] = useState(false);
  const [openAudioModal, setOpenAudioModal] = useState(false);

  const { toggle: toggleBookmark, isBookmarked } = useBookmark(
    { id: show?.id ?? "", title: show?.title ?? "" },
    "player",
  );
  const {
    toggle: toggleLike,
    liked,
    likes,
  } = useEpisodeLike(
    { id: show?.id ?? "", title: show?.title ?? "" },
    {
      id: episode?.id ?? "",
      number: episode?.number ?? 0,
    },
  );

  if (!episode || !show) return null;

  // The canonical show URL, never window.location.href — see buildShowUrl.
  const onShare = () =>
    share({
      title: show.displayTitle,
      text: t("player_share_text", { title: show.displayTitle }),
      url: buildShowUrl(window.location.origin, show.title, show.id),
    });

  const controls: Array<VideoVerticalPlayerSideControls> = [];
  // No like on the trailer, as on the mobile rail (`trailerSideControls`).
  if (episode.number !== 0) {
    controls.push({
      icon: HeartIcon,
      fill: true,
      active: liked,
      testId: "player-like-button",
      label: (
        <AnimatePresence mode="popLayout">
          {likes !== undefined && (
            <motion.div
              className="text-center"
              key={likes}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: -8,
                position: "absolute",
              }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              {formatViewsNumber(likes) || "0"}
            </motion.div>
          )}
        </AnimatePresence>
      ),
      onClick: toggleLike,
    });
  }
  if (isShareSupported()) {
    controls.push({
      icon: CornerUpRightIcon,
      label: t("player_share_control_label"),
      onClick: onShare,
    });
  }
  if (audioTracks && audioTracks.length > 1) {
    controls.push({
      icon: LanguagesIcon,
      label: t("player_audio_control_label"),
      onClick: () => {
        setOpenAudioModal(true);
      },
    });
  }
  if (subtitles && subtitles.length > 0) {
    controls.push({
      icon: CaptionsIcon,
      label: t("player_subtitles_control_label"),
      onClick: () => {
        trackAmplitudeEvent(AmplitudeTrackingEvent.openSubtitlesMenu, {
          "series name": show.title,
          "episode number": episode.number.toString(),
          origin: "desktop column",
        });
        setOpenSubtitleModal(true);
      },
    });
  }

  return (
    <>
      <div
        className={cn(
          "relative flex h-full max-w-sm flex-col items-start gap-6 lg:max-h-256",
          className,
        )}
        {...props}
      >
        <div className="flex flex-col gap-4">
          <div className="flex w-full items-center justify-between">
            <h2 className="text-3xl font-bold">
              <Link
                to="/series/$series"
                params={{ series: series ?? "" }}
                state={{ seriesDetailsOrigin: "player title" }}
                data-test-id="player-series-title"
              >
                {show.displayTitle}
              </Link>
            </h2>
            <PlayerSideControl
              icon={BookmarkIcon}
              fill
              active={isBookmarked}
              isDisplayed
              onClick={toggleBookmark}
              label={formatViewsNumber(show.bookmarks) || "0"}
              testId="player-bookmark-button"
            />
          </div>
          <EpisodeDesktopControlsDescription
            description={show.displayDescription}
          />
        </div>
        <Separator />
        <div className="flex w-full items-start justify-center gap-10 transition-all">
          {controls.map((control) => (
            <AnimatePresence mode="wait" key={control.label?.toString()}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <PlayerSideControl {...control} isDisplayed />
              </motion.div>
            </AnimatePresence>
          ))}
        </div>
        <div className="h-full min-h-0 w-full grow">
          <Separator />
          <div className="h-full overflow-y-auto">
            <EpisodesList
              className="py-4"
              episodes={show.episodes}
              activeEpisodeIndex={swiper.activeIndex}
              onEpisodeSelect={(e, index) => onEpisodeSelect(swiper, e, index)}
            />
          </div>
        </div>
      </div>
      <PlayerPreferenceModal
        title={t("player_audio_menu_title_web")}
        open={openAudioModal}
        mediaList={audioTracks || []}
        activePlayerPreferenceIndex={hlsContext?.audioTrack ?? null}
        onOpenChange={setOpenAudioModal}
        onPlayerPreferenceChange={(audioIndex) => {
          if (hlsContext && audioTracks) {
            hlsContext.audioTrack = audioIndex;
            setAudioPreference(audioTracks[audioIndex].language);
          }
        }}
      />
      <PlayerPreferenceModal
        title={t("player_subtitles_menu_title_web")}
        open={openSubtitleModal}
        mediaList={[
          { id: -1, name: t("player_off_label_web"), language: "" },
          ...(subtitles || []),
        ]}
        activePlayerPreferenceIndex={hlsContext?.subtitleTrack ?? null}
        onOpenChange={setOpenSubtitleModal}
        onPlayerPreferenceChange={(subtitleIndex) => {
          if (hlsContext && subtitles) {
            // -1 is the "Off" row prepended above, not an index into
            // `subtitles` — it has no track to read a language from.
            const subtitle =
              subtitleIndex === -1 ? null : subtitles[subtitleIndex];
            hlsContext.subtitleTrack = subtitleIndex;
            if (subtitle) setSubtitlesPreference(subtitle.language);
            else disableSubtitles();
          }
        }}
      />
    </>
  );
};

export default EpisodeDesktopControls;
